<template>
  <div class="cc-fields">
    <b-field custom-class="is-small" label="Card Type" :addons="false">
      <b-radio v-model="internalType" :native-value="cardTypes.PERSONAL">
        Personal
      </b-radio>
      <b-radio v-model="internalType" :native-value="cardTypes.CORPORATE">
        Corporate
      </b-radio>
      <b-radio
        v-model="internalType"
        :native-value="cardTypes.PERSONAL_AND_CORPORATE"
      >
        Both
      </b-radio>
    </b-field>
    <b-field label="Card Nickname" label-position="inside">
      <b-input
        :value="nickname"
        name="cc-nickname"
        autocomplete="off"
        placeholder="i.e. Traveller Card, Leisure Card"
        required
        @input="$emit('update:nickname', $event)"
      ></b-input>
    </b-field>
    <b-field label="Credit Card Number" label-position="inside">
      <b-input
        v-cleave="masks.creditCard"
        :value="formattedNumber"
        name="cc-number"
        autocomplete="cc-fields cc-number"
        required
        @input.native="onCardNumberInput"
      ></b-input>
    </b-field>
    <div class="cc-fields-group">
      <b-field label="Expiration" label-position="inside">
        <b-input
          v-cleave="masks.expiration"
          :value="formattedExpirationDate"
          name="cc-exp"
          autocomplete="cc-fields cc-exp"
          placeholder="MM/YY"
          pattern="[0-9]{2}/[0-9]{2}"
          required
          @input.native="onCardExpirationInput"
        ></b-input>
      </b-field>
      <b-field label="CVV" label-position="inside">
        <b-input
          v-cleave="masks.cvv"
          name="cc-csc"
          autocomplete="cc-fields cc-csc"
          pattern="[0-9]{3,4}"
          required
          @input.native="onCVVInput"
        ></b-input>
      </b-field>
    </div>
    <b-field label="Cardholder Name" label-position="inside">
      <b-input
        :value="cardholderName"
        name="cc-name"
        autocomplete="cc-fields cc-name"
        required
        @input="$emit('update:cardholder-name', $event)"
      ></b-input>
    </b-field>
    <b-field label="Billing Address" label-position="inside">
      <b-input
        :value="billingAddress1"
        name="address-line1"
        autocomplete="cc-fields address-line1"
        required
        @input="$emit('update:billing-address1', $event)"
      ></b-input>
    </b-field>
    <b-field label="Billing Address 2" label-position="inside">
      <b-input
        :value="billingAddress2"
        name="address-line2"
        autocomplete="cc-fields address-line2"
        placeholder="Optional"
        @input="$emit('update:billing-address2', $event)"
      ></b-input>
    </b-field>
    <div class="cc-fields-group">
      <b-field label="City" label-position="inside">
        <b-input
          :value="city"
          name="address-level2"
          autocomplete="cc-fields address-level2"
          required
          @input="$emit('update:city', $event)"
        ></b-input>
      </b-field>
      <b-field label="State\Province" label-position="inside" expanded>
        <b-input
          :value="state"
          name="address-level1"
          autocomplete="cc-fields address-level1"
          placeholder="Optional"
          @input="$emit('update:state', $event)"
        ></b-input>
      </b-field>
    </div>
    <div class="cc-fields-group">
      <b-field label="Country" label-position="inside" expanded>
        <b-input
          :value="country"
          name="country"
          autocomplete="cc-fields country"
          required
          @input="$emit('update:country', $event)"
        ></b-input>
      </b-field>
      <b-field label="Postal Code" label-position="inside" expanded>
        <b-input
          :value="postalCode"
          name="postal-code"
          autocomplete="cc-fields postal-code"
          required
          @input="$emit('update:postal-code', $event)"
        ></b-input>
      </b-field>
    </div>
    <b-field
      v-if="hasAcknowledgement"
      label="Acknowledgement"
      custom-class="is-small"
      class="cc-fields-acknowledgement"
    >
      <b-checkbox
        id="ccAcknowledgement"
        v-model="acknowledged"
        size="is-small"
        class="cc-fields-acknowledgement-checkbox"
        type="is-dark"
        required
      >
        I understand that this form is legally binding, and that I affirm that
        the above information is accurate and the travel agency and/or its
        approved suppliers of travel services are authorized to charge my card
        per my approval.
      </b-checkbox>
    </b-field>
    <b-field v-if="hasSignature" label="Signature" custom-class="is-small">
      <div class="cc-fields-signature">
        <VueSignaturePad
          ref="signaturePad"
          width="100%"
          height="150px"
          class="cc-fields-signature-pad"
        />
        <div class="has-text-right">
          <a @click="clearSignature"><small>[clear]</small></a>
        </div>
        <div class="cc-fields-signature-border"></div>
      </div>
    </b-field>
  </div>
</template>

<script>
import cleave from "@/directives/cleave";
import VueSignaturePad from "vue-signature-pad";
import cardTypes from "@/constants/cardTypes";

export default {
  directives: { cleave },
  components: {
    VueSignaturePad
  },
  props: {
    type: {
      type: String,
      default: null
    },
    nickname: {
      type: String,
      default: ""
    },
    number: {
      type: String,
      default: ""
    },
    cvv: {
      type: String,
      default: ""
    },
    expirationMonth: {
      type: String,
      default: null
    },
    expirationYear: {
      type: String,
      default: null
    },
    cardholderName: {
      type: String,
      default: ""
    },
    billingAddress1: {
      type: String,
      default: ""
    },
    billingAddress2: {
      type: String,
      default: ""
    },
    city: {
      type: String,
      default: ""
    },
    state: {
      type: String,
      default: ""
    },
    country: {
      type: String,
      default: ""
    },
    postalCode: {
      type: String,
      default: ""
    },
    hasAcknowledgement: {
      type: Boolean,
      default: true
    },
    hasSignature: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      acknowledged: false,
      internalFormattedExpirationDate: "",
      internalFormattedNumber: "",
      cardTypes,
      masks: {
        creditCard: {
          creditCard: true,
          onCreditCardTypeChanged: type => {
            // TODO: show credit card icon
            // Cleave.js detects these types of cards:
            // uatp, amex, diners, discover, mastercard, dankort, instapayment,
            // jcb15, jcb, maestro, visa, mir, unionPay, unknown
            this.cardType = type !== "unknown" ? type : null;
          }
        },
        expiration: {
          date: true,
          // TODO: set min date
          // Example: `dateMin: "19-01"` (YY-MM)
          datePattern: ["m", "y"]
        },
        cvv: { blocks: [4], numericOnly: true }
      }
    };
  },
  computed: {
    internalType: {
      get() {
        return this.type;
      },
      set(value) {
        this.$emit("update:type", value);
      }
    },
    formattedExpirationDate() {
      // Use the cached formatted value when both expiration month and year are
      // null. This usually occurs when the user backspaces after entering a
      // valid month/year.
      return this.expirationMonth && this.expirationYear
        ? `${this.expirationMonth}/${this.expirationYear}`
        : this.internalFormattedExpirationDate;
    },
    formattedNumber() {
      return this.internalFormattedNumber.replace(/\s/g, "") == this.number
        ? this.internalFormattedNumber
        : this.number;
    },
    formCompleted() {
      const requiredFields = [
        "type",
        "nickname",
        "number",
        "cvv",
        "expirationMonth",
        "expirationYear",
        "cardholderName",
        "billingAddress1",
        "city",
        "country",
        "postalCode"
      ];
      const allFieldsCompleted = requiredFields.every(key => this[key]);
      return (
        allFieldsCompleted &&
        (!this.hasAcknowledgement || this.acknowledged) &&
        (!this.hasSignature ||
          (this.$refs.signaturePad && !this.$refs.signaturePad.isEmpty()))
      );
    }
  },
  watch: {
    formCompleted(value) {
      this.$emit("update:form-completed", value);
    }
  },
  methods: {
    createSignatureBase64() {
      const { data } = this.$refs.signaturePad.saveSignature();
      return data.split(",")[1];
    },
    createSignatureBlob() {
      const { data } = this.$refs.signaturePad.saveSignature();
      const [header, base64Data] = data.split(",");
      var [type] = header.substring(5).split(";");
      const binary = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      return new Blob([binary], { type });
    },
    clearSignature() {
      this.$refs.signaturePad.clearSignature();
    },
    onCVVInput(e) {
      this.$emit("update:cvv", e.target._vCleave.getRawValue());
    },
    onCardNumberInput(e) {
      this.internalFormattedNumber = e.target._vCleave.getFormattedValue();
      this.$emit("update:number", e.target._vCleave.getRawValue());
    },
    onCardExpirationInput(e) {
      this.internalFormattedExpirationDate = e.target._vCleave.getFormattedValue();
      // Returns "YY-MM-00" if value is valid, otherwise "".
      const isoDate = e.target._vCleave.getISOFormatDate();
      const [expirationYear, expirationMonth] = isoDate.split("-");
      this.$emit("update:expiration-month", expirationMonth || null);
      this.$emit("update:expiration-year", expirationYear || null);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/scss/colors";

.cc-fields {
  &-group {
    display: flex;
    flex-shrink: 0;
    justify-content: space-between;
    & > * {
      margin-bottom: 20px;
      width: calc(50% - 10px);
    }
  }
  .field:not(:last-child) {
    margin-bottom: 20px;
  }
  &-card {
    position: relative;
    &-type {
      position: absolute;
      right: 0;
      top: 0;
      z-index: 2;
    }
  }
  &-acknowledgement {
    &-checkbox.checkbox {
      align-items: flex-start;
      color: #666;
      font-size: 13px;
      top: 3px;
    }
  }
  &-signature {
    &-pad {
      background-color: #fff;
    }
    &-border {
      border-bottom: 2px dashed #aaa;
      margin: -35px 30px 50px 30px;
    }
  }
}
</style>
